import React, { useEffect, useMemo, useState } from 'react';
import { C, CARD_W, imgUrl, normalizeImgField } from '../flowCanvas.constants';

export const IMAGE_OVERLAY_CARD_H = 196;

export default function ImageOverlayCardNode({
  item,
  index,
  selected,
  onClick,
  onDelete,
  overrideX,
  overrideY,
  assetFolder,
  campaignId,
  visualStyle = 'default',
  label = 'Image',
  fallbackTitleBase,
  typeLabel = 'Image',
  behaviourLabel,
  showPositionInputs = false,
  onFieldChange,
}) {
  const W = CARD_W;
  const H = IMAGE_OVERLAY_CARD_H;
  const x = overrideX;
  const y = overrideY;
  const left = x - W / 2;
  const top = y - H / 2;

  const imgNorm = normalizeImgField(item?.filename);
  const imgSrc = imgNorm.srcUrl
    ? imgNorm.srcUrl
    : imgNorm.name ? imgUrl(imgNorm.name, imgNorm.isDefault, assetFolder, campaignId) : null;

  const normalizedFallbackTitleBase = fallbackTitleBase
    || (typeof label === 'string' ? label.replace(/\s+images?$/i, '').trim() : '')
    || 'Image';
  const title = item?.name || `${normalizedFallbackTitleBase} ${index + 1}`;
  const shortTitle = title.length > 28 ? `${title.slice(0, 26)}…` : title;
  const fileName = imgNorm.name || 'No image set';
  const shortFileName = fileName.length > 28 ? `${fileName.slice(0, 26)}…` : fileName;
  const isConfigured = !!imgNorm.name;
  const isWaveStyle = visualStyle === 'wave';
  const subtitle = useMemo(() => (
    behaviourLabel ? `${typeLabel} (${behaviourLabel})` : typeLabel
  ), [behaviourLabel, typeLabel]);

  const [xValue, setXValue] = useState(item?.x ?? 0);
  const [yValue, setYValue] = useState(item?.y ?? 0);

  useEffect(() => {
    setXValue(item?.x ?? 0);
  }, [item?.x]);

  useEffect(() => {
    setYValue(item?.y ?? 0);
  }, [item?.y]);

  const commitNumberField = (field, draft, fallback) => {
    if (!onFieldChange) return;
    const parsed = Number(draft);
    if (Number.isFinite(parsed)) {
      onFieldChange(field, parsed);
      return;
    }
    if (field === 'x') setXValue(fallback ?? 0);
    if (field === 'y') setYValue(fallback ?? 0);
  };

  const IMG_X = left + 8;
  const IMG_Y = isWaveStyle ? top + 36 : top + 44;
  const IMG_W = W - 16;
  const IMG_H = isWaveStyle ? 100 : (showPositionInputs ? 76 : 92);
  const clipId = `fc-image-overlay-${Math.round(x)}-${Math.round(y)}-${index}`;
  const checkerId = `fc-image-overlay-checker-${Math.round(x)}-${Math.round(y)}-${index}`;

  return (
    <g className="fc-node fc-node--image-overlay-card" onClick={onClick} style={{ cursor: 'pointer' }}>
      <defs>
        <clipPath id={clipId}>
          <rect x={IMG_X} y={IMG_Y} width={IMG_W} height={IMG_H} rx="6" />
        </clipPath>
        <pattern id={checkerId} patternUnits="userSpaceOnUse" width="8" height="8">
          <rect width="8" height="8" fill="#555" />
          <rect width="4" height="4" fill="#333" />
          <rect x="4" y="4" width="4" height="4" fill="#333" />
        </pattern>
      </defs>

      <rect x={left + 2} y={top + 4} width={W} height={H} rx="8" fill="#000" fillOpacity="0.3" />
      <rect
        x={left}
        y={top}
        width={W}
        height={H}
        rx="8"
        fill={selected ? C.cardSel : C.card}
        stroke={selected ? C.sel : (isConfigured ? C.purple : C.orange)}
        strokeWidth={selected ? 2 : 1.5}
      />

      <text
        x={left + 12}
        y={top + 18}
        fontSize="11"
        fontWeight="700"
        fill={C.text}
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        {shortTitle}
      </text>
      <text
        x={left + 12}
        y={top + 31}
        fontSize="9.5"
        fill={C.textMuted}
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        {subtitle}
      </text>

      <rect
        x={IMG_X}
        y={IMG_Y}
        width={IMG_W}
        height={IMG_H}
        rx="6"
        fill={isWaveStyle ? `url(#${checkerId})` : C.circle}
        stroke="rgba(145,70,255,0.18)"
        strokeWidth="1"
      />

      {imgSrc ? (
        <image
          href={imgSrc}
          x={IMG_X}
          y={IMG_Y}
          width={IMG_W}
          height={IMG_H}
          preserveAspectRatio="xMidYMid meet"
          clipPath={`url(#${clipId})`}
          style={{ pointerEvents: 'none' }}
        />
      ) : (
        <>
          <text
            x={x}
            y={IMG_Y + IMG_H / 2 - 6}
            textAnchor="middle"
            fontSize="26"
            fill={C.textDim}
            style={{ pointerEvents: 'none', userSelect: 'none' }}
          >
            ⊕
          </text>
          <text
            x={x}
            y={IMG_Y + IMG_H / 2 + 16}
            textAnchor="middle"
            fontSize="9"
            fill={C.textDim}
            style={{ pointerEvents: 'none', userSelect: 'none' }}
          >
            Click to set image
          </text>
        </>
      )}

      {isWaveStyle && (
        <rect
          x={IMG_X}
          y={IMG_Y}
          width={IMG_W}
          height={IMG_H}
          rx="6"
          fill="none"
          stroke={isConfigured ? C.purple : C.orange}
          strokeWidth="0.75"
          strokeOpacity="0.35"
        />
      )}

      {showPositionInputs && (
        <foreignObject x={left + 8} y={top + 130} width={W - 16} height={26}>
          <div
            xmlns="http://www.w3.org/1999/xhtml"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
              width: '100%',
              height: '100%',
            }}
          >
            <label style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', flex: 1, minWidth: 0, gap: '6px', color: 'var(--fc-text-muted)', fontSize: '10px', fontWeight: 600 }}>
              <span style={{ flexShrink: 0 }}>X</span>
              <input
                type="number"
                value={xValue}
                onChange={(e) => setXValue(e.target.value)}
                onBlur={() => commitNumberField('x', xValue, item?.x ?? 0)}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === 'Enter') e.currentTarget.blur();
                }}
                style={{
                  flex: 1,
                  minWidth: 0,
                  height: '22px',
                  boxSizing: 'border-box',
                  borderRadius: '2px',
                  border: `1px solid ${C.purple}`,
                  background: C.innerBg,
                  color: C.text,
                  padding: '0 10px',
                  fontSize: '11px',
                  fontFamily: 'PrimaryFont, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif',
                  letterSpacing: '0.02em',
                  outline: 'none',
                }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', flex: 1, minWidth: 0, gap: '6px', color: 'var(--fc-text-muted)', fontSize: '10px', fontWeight: 600 }}>
              <span style={{ flexShrink: 0 }}>Y</span>
              <input
                type="number"
                value={yValue}
                onChange={(e) => setYValue(e.target.value)}
                onBlur={() => commitNumberField('y', yValue, item?.y ?? 0)}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === 'Enter') e.currentTarget.blur();
                }}
                style={{
                  flex: 1,
                  minWidth: 0,
                  height: '22px',
                  boxSizing: 'border-box',
                  borderRadius: '2px',
                  border: `1px solid ${C.purple}`,
                  background: C.innerBg,
                  color: C.text,
                  padding: '0 10px',
                  fontSize: '11px',
                  fontFamily: 'PrimaryFont, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif',
                  letterSpacing: '0.02em',
                  outline: 'none',
                }}
              />
            </label>
          </div>
        </foreignObject>
      )}

      <text
        x={x}
        y={top + H - (isWaveStyle ? 18 : 14)}
        textAnchor="middle"
        fontSize="8.5"
        fill={C.textMuted}
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        {shortFileName}
      </text>

      {onDelete && (
        <g onClick={(e) => { e.stopPropagation(); onDelete(); }} style={{ cursor: 'pointer' }}>
          <circle cx={left + W - 12} cy={top + 12} r={10} fill="#dc2626" />
          <g transform={`translate(${left + W - 12}, ${top + 12})`} style={{ pointerEvents: 'none', userSelect: 'none' }}>
            <rect x="-2.5" y="-6" width="5" height="2.5" rx="1.2" fill="#fee2e2" />
            <rect x="-6" y="-4" width="12" height="2" rx="1" fill="#fee2e2" />
            <rect x="-4.5" y="-1.5" width="9" height="8" rx="1" fill="#fee2e2" />
            <rect x="-2" y="0" width="1.2" height="5" rx="0.6" fill="#dc2626" />
            <rect x="0.8" y="0" width="1.2" height="5" rx="0.6" fill="#dc2626" />
          </g>
        </g>
      )}
    </g>
  );
}
